import "./style.css";
import "./VoiceOptionsDisplay.css";
import React, { useContext, useEffect, useState, useCallback } from "react";
import {
  Anchor,
  Badge,
  Button,
  Col,
  Drawer,
  Input,
  Menu,
  Row,
  App,
  Empty,
  Tag,
} from "antd";
import {
  CloseOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../context/AppContextProvider";
import {
  QR_LANGUAGES,
  applyQrLanguage,
  qrLangShort,
  readStoredQrLocale,
} from "./qrLanguage";
import TagOptionsModal from "../POS/components/TagOptionsModal";
import {
  clearCartIfMenuChanged,
  getQrMenuProductIds,
  saveQrCartItems,
  saveQrMenuProductIds,
} from "./qrCartStorage";
import { showAppError } from "../../../utils/errorHandler";

const { Search } = Input;

const QRmenu = ({ categoriesProducts, companyDetails, onBackToOrderMode }) => {
  const { appConfig, setAppConfigWrapper } = useContext(AppContext);
  const { t, i18n } = useTranslation("qr");
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [productCategoriesList, setProductCategoriesList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [activeCategory, setActiveCategory] = useState(0);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [totalQuantity, setTotalQuantity] = useState(0);

  const [searchVisible, setSearchVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [selectedProductForTags, setSelectedProductForTags] = useState(null);
  const [selectedTagsForModal, setSelectedTagsForModal] = useState([]);

  // ---------- Helpers ----------

  const normalizeTagIds = useCallback((values) => {
    if (!Array.isArray(values)) return [];
    return values
      .map((value) => (value && typeof value === "object" ? value.id : value))
      .filter((value) => value !== undefined && value !== null);
  }, []);

  const resetTagModal = () => {
    setTagModalVisible(false);
    setSelectedProductForTags(null);
    setSelectedTagsForModal([]);
  };

  const getVariantKey = useCallback(
    (product) => {
      const selectedTagIds = normalizeTagIds(product?.selectedTagIds);
      const sorted = selectedTagIds.map(String).sort().join(",");
      return `${product.id}_${sorted}`;
    },
    [normalizeTagIds],
  );

  const recalcTotalQuantity = useCallback((items) => {
    const total = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    setTotalQuantity(total);
  }, []);

  const saveCart = useCallback(
    (items) => {
      setCart(items);
      recalcTotalQuantity(items);
      if (typeof window !== "undefined") {
        saveQrCartItems(items);
      }
    },
    [recalcTotalQuantity],
  );

  const handleBackToOrderMode = () => {
    if (onBackToOrderMode) {
      onBackToOrderMode();
    }
  };

  // ---------- Data initialization ----------

  useEffect(() => {
    const categories = [
      { id: 0, name: t("all"), name_ar: t("all", { lng: "ar" }) },
    ];
    let products = [];

    categoriesProducts.forEach((category) => {
      if (category.products.length > 0) {
        categories.push({
          id: category.id,
          name: category.name,
          name_ar: category.name_ar,
        });
        products = [...products, ...category.products];
      }
    });

    const menuProductIds = getQrMenuProductIds(categoriesProducts);
    const cartWasCleared = clearCartIfMenuChanged(menuProductIds);

    saveQrMenuProductIds(menuProductIds);
    setProductCategoriesList(categories);
    setProductsList(products);

    if (cartWasCleared) {
      setCart([]);
      recalcTotalQuantity([]);
    }
  }, [t, categoriesProducts, recalcTotalQuantity]);

  useEffect(() => {
    const storedCart = localStorage.getItem("cart_items");
    if (storedCart) {
      try {
        const cartItems = JSON.parse(storedCart);
        const menuProductIds = getQrMenuProductIds(categoriesProducts);
        const cartWasCleared = clearCartIfMenuChanged(menuProductIds);

        if (cartWasCleared) {
          setCart([]);
          recalcTotalQuantity([]);
        } else if (Array.isArray(cartItems) && cartItems.length) {
          const normalizedCart = cartItems.map((item) => {
            const selectedTagIds = normalizeTagIds(item.selectedTagIds);
            const cart_key =
              item.cart_key || getVariantKey({ ...item, selectedTagIds });
            return { ...item, selectedTagIds, cart_key };
          });

          setCart(normalizedCart);
          recalcTotalQuantity(normalizedCart);
        }
      } catch (error) {
        setCart([]);
        recalcTotalQuantity([]);
        saveQrCartItems([]);
      }
    }

  }, [
    categoriesProducts,
    getVariantKey,
    normalizeTagIds,
    recalcTotalQuantity,
  ]);

  // ---------- UI handlers ----------

  const onChangeSearchValue = (e) => setSearch(e.target.value);

  const cartReducer = (product, action) => {
    let cartNew = [...cart];

    const variantKey =
      product.cart_key || getVariantKey(product) || String(product.id);

    let indexFound = cartNew.findIndex((p) => p.cart_key === variantKey);

    if (indexFound === -1) {
      indexFound = cartNew.findIndex(
        (p) =>
          p.id === product.id &&
          (!p.selectedTagIds || p.selectedTagIds.length === 0) &&
          (!product.selectedTagIds || product.selectedTagIds.length === 0),
      );
    }

    if (action === "add") {
      if (indexFound === -1) {
        const cartItem = {
          ...product,
          quantity: 1,
          selectedTagIds: normalizeTagIds(product.selectedTagIds),
          cart_key: variantKey,
        };

        const nameToShow =
          appConfig.direction === "rtl" ? cartItem.name : cartItem.name_en;
        message.success(
          appConfig.direction === "rtl"
            ? `${t("successfully_added")} ${nameToShow}`
            : `${nameToShow} ${t("successfully_added")}`,
        );
        cartNew.push(cartItem);
      } else {
        const item = cartNew[indexFound];

        if (item.is_stockable && item.stock <= item.quantity) {
          showAppError(t("insufficient_quantity"));
        } else {
          const updated = { ...item, quantity: item.quantity + 1 };
          cartNew[indexFound] = updated;

          const nameToShow =
            appConfig.direction === "rtl" ? updated.name : updated.name_en;
          message.success(
            appConfig.direction === "rtl"
              ? `${t("successfully_added")} ${nameToShow}`
              : `${nameToShow} ${t("successfully_added")}`,
          );
        }
      }
    }

    if (action === "inc" && indexFound !== -1) {
      const item = cartNew[indexFound];
      if (item.is_stockable && item.stock <= item.quantity) {
        showAppError(t("insufficient_quantity"));
      } else {
        cartNew[indexFound] = { ...item, quantity: item.quantity + 1 };
      }
    }

    if (action === "dec" && indexFound !== -1) {
      const item = cartNew[indexFound];
      const newQty = item.quantity - 1;

      if (newQty <= 0) {
        cartNew.splice(indexFound, 1);
      } else {
        cartNew[indexFound] = { ...item, quantity: newQty };
      }
    }

    saveCart(cartNew);
  };

  const handleAddClick = (product) => {
    if (Array.isArray(product.tags) && product.tags.length > 0) {
      setSelectedProductForTags(product);
      setSelectedTagsForModal([]);
      setTagModalVisible(true);
    } else {
      cartReducer(product, "add");
    }
  };

  const handleAddToCartFromModal = () => {
    if (!selectedProductForTags) return;

    const productWithTags = {
      ...selectedProductForTags,
      selectedTagIds: normalizeTagIds(selectedTagsForModal),
    };

    cartReducer(productWithTags, "add");

    resetTagModal();
  };

  const filteredProducts = productsList.filter((product) => {
    if (!search) return true;
    const nameEn = (product.name_en || "").toLowerCase();
    return nameEn.includes(search.toLowerCase());
  });

  // ---------- Render ----------

  return (
    <div style={{ direction: appConfig.direction }}>
      {/* Header / Search */}
      {searchVisible ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            columnGap: "10px",
            boxShadow: "var(--color-shadow-soft) 5px 10px 80px 0px",
            padding: "10px",
          }}
        >
          <Button
            type="primary"
            onClick={() => setSearchVisible(false)}
            icon={<CloseOutlined />}
            className="app-square-40"
          />

          <Search
            onChange={onChangeSearchValue}
            allowClear
            placeholder={t("search_product_name_here")}
            size="large"
            enterButton
            style={{ width: "calc(100% - 50px)" }}
          />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            columnGap: "10px",
            boxShadow: "var(--color-shadow-soft) 5px 10px 80px 0px",
            padding: "10px",
          }}
        >
          <div
            className="app-flex-align-center-gap-10"
          >
            <img
              alt={companyDetails.company_name}
              src={
                companyDetails.logo
                  ? companyDetails.logo
                  : "/images/image-not-found.png"
              }
              style={{
                width: "100%",
                maxWidth: 40,
                height: "auto",
                overflow: "hidden",
                borderRadius: "35rem",
              }}
            />
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: "capitalize",
                  maxWidth: 150,
                  lineHeight: 1,
                  marginTop: 3,
                }}
              >
                {companyDetails.company_name}
              </div>
            </div>
          </div>

          <div className="app-flex-gap-10">
            <Button
              type="primary"
              onClick={() => setSearchVisible(true)}
              icon={<SearchOutlined />}
              className="app-square-40"
            />
            <Button
              type="primary"
              onClick={() => setDrawerVisible(true)}
              className="app-square-40"
            >
              {qrLangShort(i18n.language)}
            </Button>
            {onBackToOrderMode && (
              <Button
                type="default"
                onClick={handleBackToOrderMode}
                style={{ height: 40 }}
              >
                {t("change_mode")}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Body */}
      {productCategoriesList.length < 2 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(100vh - 110px)",
          }}
        >
          <Empty />
        </div>
      ) : (
        <>
          {/* Category Tabs (Anchor) */}
          <Anchor
            className="qr-anchor"
            direction="horizontal"
            style={{
              padding: "10px",
              backgroundColor: "var(--color-bg)",
              overflowX: "auto",
              whiteSpace: "nowrap",
            }}
            items={productCategoriesList.map((category) => {
              const isActive = Number(activeCategory) === category.id;
              return {
                key: category.id,
                href: `#category-${category.id}`,
                title: (
                  <div
                    style={{
                      background: isActive
                        ? "var(--color-primary)"
                        : "var(--color-white)",
                      color: isActive
                        ? "var(--color-white)"
                        : "var(--color-black)",
                      padding: "5px 15px",
                      borderRadius: "35rem",
                      cursor: "pointer",
                      display: "inline-block",
                    }}
                  >
                    {appConfig.direction === "rtl"
                      ? category.name_ar
                      : category.name}
                  </div>
                ),
              };
            })}
            onClick={(e, link) => {
              e.preventDefault();
              const href = link?.href;
              if (!href) return;
              const id = Number(href.replace("#category-", ""));
              if (!Number.isNaN(id)) setActiveCategory(id);
            }}
          />

          {/* Product Grid */}
          <Row style={{ paddingBottom: 60 }}>
            {filteredProducts.map((product) => {
              const inActiveCategory =
                Number(activeCategory) === 0 ||
                Number(activeCategory) === product.category_id;

              if (!inActiveCategory) return null;

              const outOfStock =
                product.is_stockable && Number(product.stock) === 0;

              const canClick =
                !product.is_stockable || Number(product.stock) > 0;

              return (
                <Col
                  xs={24}
                  sm={12}
                  md={12}
                  lg={8}
                  xl={6}
                  xxl={6}
                  key={product.id}
                >
                  <div style={{ padding: "10px" }}>
                    <div
                      className="qr-product-card"
                      style={{
                        position: "relative",
                        borderRadius: "16px",
                        padding: "10px",
                        backgroundColor: "var(--color-white)",
                        boxShadow: "0 10px 24px var(--color-shadow-soft)",
                        border: "1px solid var(--color-bg)",
                        display: "flex",
                        columnGap: "10px",
                        transition:
                          "transform 0.15s ease, box-shadow 0.15s ease",
                        cursor: canClick ? "pointer" : "not-allowed",
                        opacity: canClick ? 1 : 0.7,
                      }}
                    >
                      {outOfStock && (
                        <div
                          style={{
                            position: "absolute",
                            top: 10,
                            left: 10,
                            zIndex: 2,
                          }}
                        >
                          <Tag color="red">{t("out_of_stock")}</Tag>
                        </div>
                      )}

                      <img
                        alt={product.name_en}
                        src={
                          product.image
                            ? product.image
                            : "/images/image-not-found.png"
                        }
                        style={{
                          width: 96,
                          height: 96,
                          borderRadius: "12px",
                          border: "1px solid var(--color-border)",
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              marginBottom: 4,
                            }}
                          >
                            {appConfig.direction === "rtl"
                              ? product.name
                              : product.name_en}
                          </div>
                          {companyDetails?.is_calories &&
                            product.calories_info && (
                              <div
                                style={{
                                  fontSize: 12,
                                  marginTop: 2,
                                  color: "var(--color-text-muted)",
                                  fontWeight: 500,
                                }}
                              >
                                {t("calories")}: {product.calories_info}
                              </div>
                            )}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              width: "100%",
                              fontSize: 12,
                              lineHeight: 1,
                              fontWeight: 600,
                              marginTop: 8,
                            }}
                          >
                            <div>
                              <CurrencyFormatter value={product.price} />
                            </div>
                            <Button
                              type="primary"
                              style={{
                                cursor: canClick ? "pointer" : "not-allowed",
                                backgroundColor: canClick
                                  ? ""
                                  : "var(--color-border)",
                              }}
                              onClick={() =>
                                canClick ? handleAddClick(product) : undefined
                              }
                            >
                              {t("add")}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>

          {/* Bottom Bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              columnGap: "10px",
              boxShadow: "1px -2px 4px 0 var(--color-shadow-soft)",
              padding: "10px",
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "var(--color-white)",
              zIndex: 9,
            }}
          >
            {companyDetails?.orders?.length > 0 && (
              <Button
                type="primary"
                style={{
                  padding: "0 10px",
                  height: 40,
                  lineHeight: 1,
                  display: "flex",
                  alignItems: "center",
                  columnGap: "7px",
                }}
                onClick={() => navigate("/qr/history")}
              >
                <span>{t("your_order")}</span>
                <UnorderedListOutlined
                  style={{ fontSize: 25, lineHeight: 0 }}
                />
              </Button>
            )}

            <div
              style={{
                fontSize: "11px",
                color: "var(--color-text-muted)",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              {t("powered_by")}{" "}
              <a
                href="https://www.anypos.ai/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "var(--color-primary)",
                  textDecoration: "none",
                  fontWeight: 500,
                }}
              >
                AnyPOS
              </a>
            </div>

            <Button
              type="primary"
              style={{
                padding: "0 10px",
                height: 40,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                columnGap: "7px",
              }}
              onClick={() => navigate("/qr/cart")}
            >
              <span>{t("cart")}</span>
              <Badge count={totalQuantity}>
                <ShoppingCartOutlined
                  style={{
                    fontSize: 25,
                    lineHeight: 0,
                    color: "var(--color-white)",
                  }}
                />
              </Badge>
            </Button>
          </div>
        </>
      )}

      {/* Language Drawer */}
      <Drawer
        title={t("select_language")}
        placement={appConfig.direction === "rtl" ? "left" : "right"}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        styles={{ body: { padding: 0 } }}
        closable={false}
        extra={
          <Button
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={() => {
              setDrawerVisible(false);
            }}
          />
        }
      >
        <Menu
          selectedKeys={[readStoredQrLocale() || "en"]}
          items={QR_LANGUAGES.map((language) => ({
            key: language.code,
            label: language.label,
            onClick: () => {
              applyQrLanguage(language.code, setAppConfigWrapper);
              setDrawerVisible(false);
            },
          }))}
        />
      </Drawer>

      {/* Tag selection modal */}
      <TagOptionsModal
        t={t}
        appConfig={appConfig}
        open={tagModalVisible}
        onClose={resetTagModal}
        product={selectedProductForTags}
        selected={selectedTagsForModal}
        onChangeSelected={setSelectedTagsForModal}
        onConfirm={handleAddToCartFromModal}
        title={t("select_options")}
        confirmText={selectedTagsForModal.length > 0 ? t("add") : t("skip")}
        cancelText={t("cancel")}
        width={380}
      />
    </div>
  );
};

export default QRmenu;
